import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import {
  CloverPaymentService,
  CloverElement,
  CloverElementStyles,
  CloverFieldEvent,
  CloverInstance,
  CloverTokenResult
} from '../../../core/services/clover/clover-payment.service';

const IFRAME_STYLES: CloverElementStyles = {
  input: {
    fontSize: '14px',
    fontFamily: 'var(--font-family-primary, Satoshi), sans-serif',
    color: '#333',
    padding: '0'
  }
};

@Component({
  selector: 'app-clover-payment',
  standalone: true,
  templateUrl: './clover-payment.component.html',
  styleUrl: './clover-payment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CloverPaymentComponent implements OnInit, OnDestroy {
  private static readonly TOKEN_TIMEOUT_MS = 30_000;

  private clover: CloverInstance | null = null;
  private cardNumberElement: CloverElement | null = null;
  private cardDateElement: CloverElement | null = null;
  private cardCvvElement: CloverElement | null = null;
  private cardPostalCodeElement: CloverElement | null = null;

  readonly isLoading = signal(true);
  readonly error = signal<string | null>(null);
  readonly ready = signal(false);

  readonly cardNumberError = signal<string | null>(null);
  readonly cardDateError = signal<string | null>(null);
  readonly cardCvvError = signal<string | null>(null);
  readonly cardPostalCodeError = signal<string | null>(null);

  constructor(private cloverPaymentService: CloverPaymentService) {}

  async ngOnInit(): Promise<void> {
    try {
      this.clover = await this.cloverPaymentService.init();
      const elements = this.clover.elements();

      this.cardNumberElement = elements.create('CARD_NUMBER', IFRAME_STYLES);
      this.cardDateElement = elements.create('CARD_DATE', IFRAME_STYLES);
      this.cardCvvElement = elements.create('CARD_CVV', IFRAME_STYLES);
      this.cardPostalCodeElement = elements.create('CARD_POSTAL_CODE', IFRAME_STYLES);

      this.cardNumberElement.mount('#card-number');
      this.cardDateElement.mount('#card-date');
      this.cardCvvElement.mount('#card-cvv');
      this.cardPostalCodeElement.mount('#card-postal-code');

      this.attachValidationListeners();

      this.isLoading.set(false);
      this.ready.set(true);
    } catch (err) {
      console.error('Error initializing Clover:', err);
      this.error.set('Failed to load secure payment fields. Please try again later.');
      this.isLoading.set(false);
    }
  }

  ngOnDestroy(): void {
    this.cardNumberElement?.destroy();
    this.cardDateElement?.destroy();
    this.cardCvvElement?.destroy();
    this.cardPostalCodeElement?.destroy();

    this.cardNumberElement = null;
    this.cardDateElement = null;
    this.cardCvvElement = null;
    this.cardPostalCodeElement = null;
    this.clover = null;

    this.cloverPaymentService.destroy();
  }

  public async createToken(): Promise<CloverTokenResult> {
    if (!this.clover) {
      console.warn('[CloverPayment] createToken called but Clover not initialized');
      return { errors: { message: 'Clover not initialized' } };
    }

    console.log('[CloverPayment] createToken — calling SDK…');
    let timerId: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
      timerId = setTimeout(
        () => reject(new Error('Clover token creation timed out')),
        CloverPaymentComponent.TOKEN_TIMEOUT_MS
      );
    });

    try {
      const result = await Promise.race([this.clover.createToken(), timeout]);
      console.log('[CloverPayment] createToken — SDK resolved:', result);
      return result;
    } finally {
      clearTimeout(timerId!);
    }
  }

  private attachValidationListeners(): void {
    const handleEvent = (event: CloverFieldEvent): void => {
      this.cardNumberError.set(event.CARD_NUMBER?.touched ? (event.CARD_NUMBER.error ?? null) : null);
      this.cardDateError.set(event.CARD_DATE?.touched ? (event.CARD_DATE.error ?? null) : null);
      this.cardCvvError.set(event.CARD_CVV?.touched ? (event.CARD_CVV.error ?? null) : null);
      this.cardPostalCodeError.set(event.CARD_POSTAL_CODE?.touched ? (event.CARD_POSTAL_CODE.error ?? null) : null);
    };

    this.cardNumberElement?.addEventListener('change', handleEvent);
    this.cardNumberElement?.addEventListener('blur', handleEvent);
    this.cardDateElement?.addEventListener('change', handleEvent);
    this.cardDateElement?.addEventListener('blur', handleEvent);
    this.cardCvvElement?.addEventListener('change', handleEvent);
    this.cardCvvElement?.addEventListener('blur', handleEvent);
    this.cardPostalCodeElement?.addEventListener('change', handleEvent);
    this.cardPostalCodeElement?.addEventListener('blur', handleEvent);
  }
}
